import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@retro-ia/shared/axios";
import type { ActividadAprendiz } from "@/hooks/actividadAprendiz/useActividadAprendiz";

/**
 * Datos completos de una actividad, derivados de la respuesta de actividadAprendiz.
 * Incluye la actividad-programa con su programa (cerebro, limiteIntentos) y el último intento.
 */
export interface ActividadCompletaData {
  actividadAprendiz: ActividadAprendiz;
  /** Nombre del cerebro (modelo IA) del programa */
  cerebro: string;
  /** Objeto actividad-programa con datos de la actividad */
  actividad: ActividadAprendiz["actividadPrograma"] & {
    id: number;
    nombre: string;
    archivoLimpio: { url: string; llave: string; nombre: string };
    archivoWord?: { llave: string; nombre: string; url?: string } | null;
    limiteIntentos: number;
    programa: {
      id: number;
      cerebro: string;
      limiteIntentos: number;
    };
  };
  programaId: number;
}

/**
 * Hook que obtiene los datos completos de una actividad para un aprendiz.
 * Diseñado para usarse como fallback cuando no hay state de React Router (recarga de página).
 *
 * @param actividadId - ID de la actividad-programa
 * @param aprendizId - ID del aprendiz
 * @param enabled - Solo hacer fetch cuando no hay state de React Router
 */
export function useActividadCompleta(
  actividadId: number,
  aprendizId: number | undefined,
  enabled: boolean,
) {
  return useQuery<ActividadCompletaData | null>({
    queryKey: ["actividadCompleta", actividadId, aprendizId],
    queryFn: async () => {
      if (!aprendizId) return null;

      try {
        // Usa el mismo endpoint que useActividadAprendiz pero con tipado extendido
        const response = await axiosInstance.get(
          `/actividades-aprendiz/actividad/${actividadId}/aprendiz/${aprendizId}`,
        );
        const data = response.data;

        if (!data || !data.actividadPrograma) {
          return null;
        }

        return {
          actividadAprendiz: data,
          cerebro: data.actividadPrograma.programa?.cerebro ?? "",
          actividad: data.actividadPrograma,
          programaId: data.actividadPrograma.idPrograma,
        };
      } catch (error: any) {
        if (error?.response?.status === 400 || error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && !!actividadId && aprendizId != null,
    staleTime: 30_000,
    retry: false,
  });
}
